import { DecoratorNode, $applyNodeReplacement, createCommand } from 'lexical';
import * as React from 'react';
import {
  buildEmbedSrc, parseEmbedSrc, YOUTUBE_ID_RE, VIMEO_ID_RE,
} from '../../../utils/videoEmbeds';
import DocVideoEmbedComponent from './DocVideoEmbedComponent';

/**
 * A YouTube (nocookie) / Vimeo embed block in a document.
 *
 * Identity is `{ provider, videoId, start }` — never a raw URL: the iframe
 * src is always REBUILT via utils/videoEmbeds.js buildEmbedSrc, both here and
 * in the sanitizer, so no attacker-controlled URL string survives anywhere in
 * the pipeline. The desktop CSP's `frame-src` allowlists exactly the two
 * provider hosts (mediaCspGuard.test.js ties them together).
 *
 * Saved-HTML shape (exportDOM writes; sanitizeHtml.js sanitizeIframe
 * validates/rebuilds; importDOM claims):
 *   <iframe src="<canonical embed src>" width="560" height="315"
 *           frameborder="0" allowfullscreen></iframe>
 * — attribute-for-attribute what sanitizeIframe forces, so sanitizing our own
 * output is a no-op. importDOM claims ONLY iframes whose src parseEmbedSrc()s
 * (that includes the LEGACY `youtube.com/embed/` shape the sanitizer used to
 * allowlist — those canonicalize onto youtube-nocookie on the next save);
 * every other iframe stays unclaimed.
 *
 * REGISTERED BEFORE ANY INSERTION UI EXISTS — same LWW-autosave data-loss
 * reasoning as DocImageNode (see that file). Do not wire commands here yet.
 *
 * A node whose fields fail validation (only reachable through hand-mangled
 * JSON) keeps its block but renders nothing and exports an empty <span>,
 * which the sanitizer unwraps — it degrades, it never emits a bad src.
 */

/** Payload: `{ url }` to parse+insert directly, or undefined to open the
 *  inline URL prompt. Registered ONLY by components/ui/DocMediaPlugin.js
 *  (media-enabled hosts); dispatched by the slash menu's YouTube item and by
 *  the prompt's own submit. */
export const INSERT_DOC_VIDEO_COMMAND = createCommand('INSERT_DOC_VIDEO_COMMAND');

function validVideoId(provider, id) {
  if (provider === 'youtube') return YOUTUBE_ID_RE.test(id || '');
  if (provider === 'vimeo') return VIMEO_ID_RE.test(id || '');
  return false;
}

function $convertDocVideoElement(domNode) {
  const parsed = parseEmbedSrc(domNode.getAttribute('src') || '');
  if (!parsed) return null;
  return {
    node: $createDocVideoEmbedNode({
      provider: parsed.provider,
      videoId: parsed.id,
      start: parsed.start,
    }),
  };
}

export class DocVideoEmbedNode extends DecoratorNode {
  __provider; // 'youtube' | 'vimeo'
  __videoId; // parse-shaped id (YOUTUBE_ID_RE / VIMEO_ID_RE), '' if invalid
  __start; // integer seconds >= 0 (youtube only; 0 = none)

  static getType() {
    return 'doc-video';
  }

  static clone(node) {
    return new DocVideoEmbedNode(node.__provider, node.__videoId, node.__start, node.__key);
  }

  constructor(provider, videoId, start, key) {
    super(key);
    // An UNKNOWN provider degrades the whole node (id -> ''), it is never
    // reinterpreted as some other provider's id.
    const known = provider === 'youtube' || provider === 'vimeo';
    this.__provider = known ? provider : 'youtube';
    this.__videoId = known && validVideoId(provider, videoId) ? videoId : '';
    this.__start =
      this.__provider === 'youtube' && Number.isInteger(start) && start > 0 ? start : 0;
  }

  static importJSON(serializedNode) {
    return $createDocVideoEmbedNode({
      provider: serializedNode.provider,
      videoId: serializedNode.videoId,
      start: serializedNode.start,
    });
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'doc-video',
      version: 1,
      provider: this.__provider,
      videoId: this.__videoId,
      start: this.__start,
    };
  }

  static importDOM() {
    return {
      iframe: (domNode) => {
        if (!domNode.getAttribute) return null;
        // Claim ONLY parseable embed srcs; foreign iframes stay unclaimed
        // (and are dropped by the sanitizer before ever reaching here on the
        // save/load path).
        if (!parseEmbedSrc(domNode.getAttribute('src') || '')) return null;
        return { conversion: $convertDocVideoElement, priority: 1 };
      },
    };
  }

  exportDOM() {
    const src = buildEmbedSrc({
      provider: this.__provider,
      id: this.__videoId,
      start: this.__start,
    });
    if (!src) {
      // Degraded node (invalid id) — exports an empty inline element the
      // sanitizer unwraps to nothing rather than a broken iframe.
      return { element: document.createElement('span') };
    }
    const element = document.createElement('iframe');
    element.setAttribute('src', src);
    element.setAttribute('width', '560');
    element.setAttribute('height', '315');
    element.setAttribute('frameborder', '0');
    element.setAttribute('allowfullscreen', '');
    return { element };
  }

  isInline() {
    return false;
  }

  createDOM(config) {
    const span = document.createElement('span');
    const className = config.theme.docVideo;
    if (className) span.className = className;
    return span;
  }

  updateDOM() {
    return false;
  }

  getProvider() {
    return this.getLatest().__provider;
  }

  getVideoId() {
    return this.getLatest().__videoId;
  }

  getStart() {
    return this.getLatest().__start;
  }

  decorate() {
    return (
      <DocVideoEmbedComponent
        nodeKey={this.__key}
        provider={this.__provider}
        videoId={this.__videoId}
        start={this.__start}
      />
    );
  }
}

export function $createDocVideoEmbedNode({ provider = 'youtube', videoId = '', start = 0 } = {}) {
  return $applyNodeReplacement(new DocVideoEmbedNode(provider, videoId, start));
}

export function $isDocVideoEmbedNode(node) {
  return node instanceof DocVideoEmbedNode;
}
